import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ChatInterface from './ChatInterface';
import LessonCompletionModal from './LessonCompletionModal';
import { useWebSocket } from '../../hooks/useWebSocket';
import { CourseWithUnits, DetailedLesson } from '../../types';
import { api } from '../../services/api';
import AnimatedPoints from '../shared/AnimatedPoints';

interface LessonPopoverProps {
  lesson: DetailedLesson;
  trigger: React.ReactNode;
}

function LessonPopover({ lesson, trigger }: LessonPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="lesson-popover-container">
      <div
        ref={triggerRef}
        className="lesson-popover-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        {trigger}
      </div>

      {isOpen && (
        <div ref={popoverRef} className="lesson-popover">
          <div className="lesson-popover-header">
            <h3 style={{ margin: 0 }}>{lesson.title}</h3>
            <button
              className="lesson-popover-close"
              onClick={() => setIsOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="lesson-popover-content">
            <p className="lesson-description">{lesson.description}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function LessonPage() {
  const { lessonId, chatId } = useParams<{ lessonId: string; chatId: string }>();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<DetailedLesson | null>(null);
  const [course, setCourse] = useState<CourseWithUnits | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [points, setPoints] = useState(0);
  const previousLiveCompletionCountRef = useRef(0);

  const { messages, connectionStatus, sendMessage, completedSections, currentSectionName, liveLessonCompletionCount } = useWebSocket(
    lessonId || '',
    isAuthenticated ? chatId || '' : ''
  );
  const completedSectionCount = Object.keys(completedSections).length;

  useEffect(() => {
    if (!lessonId) return;

    const load = async () => {
      try {
        const me = await api.me();
        if (!me?.authenticated) {
          try {
            const { course_id } = await api.getLessonCourse(lessonId);
            navigate(`/register?redirect=${encodeURIComponent(`/course/${course_id}`)}`, { replace: true });
          } catch {
            navigate('/register?redirect=/browse', { replace: true });
          }
          return;
        }

        setIsAuthenticated(true);
        setIsAdmin(!!me.user?.is_admin);
        setPoints(me.user.points);

        const foundLesson = await api.getLesson(lessonId);

        setLesson(foundLesson);

        if (foundLesson.course_id) {
          api.getCourse(foundLesson.course_id)
            .then(setCourse)
            .catch(() => {});
        }
      } catch (error) {
        console.error('Error loading lesson:', error);
        navigate('/');
      }
    };

    load();
  }, [lessonId, navigate]);

  useEffect(() => {
    if (liveLessonCompletionCount > previousLiveCompletionCountRef.current) {
      setShowCompletionModal(true);
    }
    previousLiveCompletionCountRef.current = liveLessonCompletionCount;
  }, [liveLessonCompletionCount]);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.me()
      .then(me => {
        if (me.authenticated) setPoints(me.user.points);
      })
      .catch(() => {});
  }, [completedSectionCount, isAuthenticated, liveLessonCompletionCount]);

  const handleSendMessage = (messageContent: string) => {
    sendMessage({ type: 'user_message', content: messageContent });
  };

  const handleSelectSection = (sectionName: string) => {
    if (connectionStatus !== 'connected') return;
    sendMessage({ type: 'section_switch', content: sectionName });
  };

  const closeCompletionModal = () => {
    setShowCompletionModal(false);
  };

  if (!lesson) {
    return <div>Loading...</div>;
  }

  return (
    <div className="app">
      <main className="lesson-main">
        <div className="lesson-interface">
          <div className="lesson-split">
            <div className="lesson-header">
              <div className="lesson-header-left">
                <Link to="/dashboard" className="navbar-brand">
                  <img src="/logo.webp" alt="TeachMeNow Logo" className="navbar-brand-logo" />
                  <span className="navbar-brand-text">TeachMeNow</span>
                </Link>
                {!showSidebar && (
                  <button
                    className="navbar-link show-sections-btn"
                    onClick={() => setShowSidebar(true)}
                    type="button"
                  >
                    <span className="show-sections-btn-full">Show Sections</span>
                    <span className="show-sections-btn-short" aria-hidden="true">Sections</span>
                  </button>
                )}
              </div>
              <div className="lesson-header-right">
                <AnimatedPoints points={points} className="lesson-points-stat" />
                {course && (
                  <Link to={`/course/${course.id}`} className="navbar-link lesson-header-course-link">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 3 }}><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
                    <span className="lesson-header-course-link-text">{course.title}</span>
                  </Link>
                )}
                <LessonPopover
                  lesson={lesson}
                  trigger={
                    <div className="navbar-link current-lesson-trigger">
                      {lesson.title}
                    </div>
                  }
                />
              </div>
            </div>

            <div className="lesson-body">
              <ChatInterface
                messages={messages}
                onSendMessage={handleSendMessage}
                connectionStatus={connectionStatus}
                currentLesson={lesson}
                chatId={chatId || ''}
                isAdmin={isAdmin}
                completedSections={completedSections}
                showSidebar={showSidebar}
                onHideSidebar={() => setShowSidebar(false)}
                currentSectionName={currentSectionName}
                onSectionSelect={handleSelectSection}
                onLessonCompletionClick={() => setShowCompletionModal(true)}
              />
            </div>
          </div>
        </div>
      </main>
      <LessonCompletionModal
        open={showCompletionModal}
        lesson={lesson}
        course={course}
        chatId={chatId || ''}
        onClose={closeCompletionModal}
      />
    </div>
  );
}

export default LessonPage;
